import debounce from "lodash/debounce";
import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { useWatchlist } from "../../hooks/useWatchlist";
import { setSearchPatientModalOpen } from "../../redux/Layout/layout.slice";
import { IAnalyticsController } from "../../hexagon/adapters/driver/analyticsController";
import { IAppNotification } from "../../types/app-notification";

interface ListData {
  data: AwaitableReturn<ISearchController["getSearch"]>["data"];
  isLoading: boolean;
}

interface IUseSearchHook {
  searchController: ISearchController;
  analyticsController: IAnalyticsController;
  watchlistController: IWatchlistController;
  appNotification: IAppNotification;
  trackWatchlistAdd: (context?: Record<string, any>) => void;
}

export const useSearchHook = ({
  searchController,
  analyticsController,
  watchlistController,
  appNotification,
  trackWatchlistAdd,
}: IUseSearchHook) => {
  const dispatch = useAppDispatch();
  const { isInWatchlist, addToWatchlist, removeFromWatchlist, isAddingList } =
    useWatchlist({
      watchlistController: watchlistController,
      appNotification: appNotification,
      analyticsController: analyticsController,
      trackWatchlistAdd: trackWatchlistAdd,
    });
  const { userClinic, selectedFloors, searchPatientModalOpen } = useAppSelector(
    (state) => ({
      userClinic: state.clinic.userClinic,
      selectedFloors: state.clinic.selectedFloors,
      searchPatientModalOpen: state.layout.searchPatientModalOpen,
    })
  );

  const [response, setResponse] = useState<ListData>({
    data: { data: [] },
    isLoading: false,
  });
  const [searchValue, setSearchValue] = useState("");

  const onChange = (val: string) => {
    setSearchValue(val);
    if (val.length > 1) {
      setResponse({
        data: {
          data: [],
        },
        isLoading: true,
      });
      setServicesValueDebounced(val);
    }
  };

  const setServicesValue = async (value: string) => {
    const { success, data } = await searchController.getSearch({
      clinicId: userClinic?.id!,
      floorIds: selectedFloors!,
      search: value,
    });
    if (success && data) {
      setResponse({
        data: {
          data: data.data,
        },
        isLoading: false,
      });
      return;
    }
    setResponse({
      data: {
        data: [],
      },
      isLoading: false,
    });
  };

  const setServicesValueDebounced = useCallback(
    debounce(setServicesValue, 500),
    []
  );

  const onSearchPatientModalOpen = () => {
    dispatch(setSearchPatientModalOpen("global"));
  };

  const onSearchPatientModalClose = () => {
    setResponse({
      data: {
        data: [],
      },
      isLoading: false,
    });
    setSearchValue("");
    dispatch(setSearchPatientModalOpen(null));
  };

  return {
    isSearchPatientModalOpen: searchPatientModalOpen,
    onSearchPatientModalOpen,
    onSearchPatientModalClose,

    searchValue,
    onChange,
    response,

    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    isAddingList,
  };
};
